import {useRef} from 'react';

import {Alert, SimpleGrid} from '@mantine/core';
import {I18n} from '@aws-amplify/core';
import {useNavigate} from 'react-router-dom';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useReconciliationReports} from '../hooks/useReconciliationReports.ts';
import {useReconciliationReportsTableColumns} from '../hooks/useReconciliationReportsTableColumns.tsx';
import {detailPath} from '../config';
import {ReconciliationReportRow} from '../types.ts';

export const RecordsTable = () => {
    const {columns} = useReconciliationReportsTableColumns();
    const navigate = useNavigate();

    const {
        items,
        visibleItems,
        keyword,
        handleFilterByKeyword,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
    } = useReconciliationReports();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const term = keyword.trim();

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load reconciliation reports')}>
                    {I18n.get('The billing API did not return the reconciliation runs for this channel. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            {/*
              NO ROW SELECTION, deliberately (FNP-535). Reports are read-only: the
              ChannelReconciler writes them through an IAM-only mutation and nothing
              edits or deletes one, so there is no batch action for a checkbox to drive.
              Row click opens the detail view; the Run Date cell is the same link for
              keyboard users.
            */}
            <MantineDataTable<ReconciliationReportRow>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, the same scope as
                // the keyword filter. Token pagination cannot reorder unseen runs.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                // The library default "No records found" contradicted the error Alert
                // above it. Each state gets its own words; the Alerts carry the detail.
                noRecordsText={isError
                    ? I18n.get('Not loaded')
                    : (term ? I18n.get('No matches') : I18n.get('No runs yet'))}
                onRowClick={({record}) => navigate(detailPath(record.id))}
            />

            {/*
              TWO EMPTY STATES (§3, §13). No runs for a channel is normal (a channel that
              is not reconciled yet, or a new one); nothing matching means the search is
              too narrow. Gated on !loading so it does not flash during the first fetch,
              and on !isError so a failed load keeps its own Alert.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert
                    color={term ? 'gray' : 'blue'}
                    title={term ? I18n.get('No matching reconciliation reports') : I18n.get('No reconciliation runs for this channel yet')}
                >
                    {term
                        ? `${I18n.get('Nothing in the loaded reconciliation reports matches')} "${term}". ${I18n.get('Clear the search to see every loaded run. A match may still be on a page that has not been loaded yet.')}`
                        : I18n.get('The reconciler writes one report per channel per day. None has been recorded for this channel, so this is not an error. Use Filter to pick another channel.')}
                </Alert>
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={I18n.get('reconciliation report')}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
